import { graphql, HttpResponse, passthrough } from 'msw';
import { http } from 'msw/core/http';

import { whoAmIHandler } from '@revelio/auth/mocks';
import { GetRevelioAdminUserDetailsHandler } from '@revelio/core';

import { reportConfigHandler } from '../next/reports/report-builder/mocks/handlers';
import companySelectionListMock from './companySelectionList';
import { filterImpactResponse } from './impactResponse';
import { usersQueryData } from './usersQuery';

export const GetAllUsersHandler = graphql.query('GetAllUsers', () => {
  return HttpResponse.json({
    data: usersQueryData,
  });
});

export const GetAllClientUsersHandler = graphql.query(
  'GetAllClientUsers',
  () => {
    return HttpResponse.json({
      data: { clients: [usersQueryData] },
    });
  }
);

export const GetAllClientsHandler = graphql.query('AllClients', () => {
  return HttpResponse.json({
    data: {
      clients: [
        {
          client_name: 'Hogwarts',
          __typename: 'Client',
        },
        {
          client_name: 'Ministry of Magic',
          __typename: 'Client',
        },
        {
          client_name: 'Durmstrang',
          __typename: 'Client',
        },
        {
          client_name: 'Revelio Labs',
          __typename: 'Client',
        },
      ],
    },
  });
});

export const GetAllCompanyListsHandler = graphql.query(
  'AllCompanyLists',
  () => {
    return HttpResponse.json({
      data: {
        company_lists: [
          {
            id: '828340263681622036',
            name: 'MASTER',
            __typename: 'CompanyList',
          },
          {
            id: '681049582353711114',
            name: 'Public Companies',
            __typename: 'CompanyList',
          },
          {
            id: '681049582353711999',
            name: 'SP 500',
            __typename: 'CompanyList',
          },
          {
            id: '681049584891297793',
            name: 'Unicorns',
            __typename: 'CompanyList',
          },
        ],
      },
    });
  }
);

export const GetClientDetailsHandler = graphql.query(
  'GetClientDetails',
  ({ variables }) => {
    const { ids } = variables;
    return HttpResponse.json({
      data: {
        clients: [
          {
            client_name: ids[0],
            company_lists: ['828340263681622036'],
            active: true,
            tabs: [
              'screener',
              'job_analysis_overview',
              'posting',
              'overview',
              'transition',
              'sentiment',
              'skill',
              'data_dictionary',
              'job_analysis',
              'talent_discovery',
            ],
            live: true,
            linkup_postings: false,
            num_seats: '0',
            __typename: 'Client',
          },
        ],
      },
    });
  }
);

// export const companySelectionListErrorHandler = rest.get(
//   `/api/filter/company`,
//   (_req, res, ctx) => {
//     return res(ctx.status(500));
//   }
// );

export const companySelectionListHandler = http.get(
  `${'https://msw-mock-filtering.api'}/api/filter/company`,
  () => {
    return HttpResponse.json(companySelectionListMock);
  }
);

const emptySelectionList = () => [];

export const SelectionListsHandler = graphql.query('SelectionLists', () => {
  return HttpResponse.json({
    data: {
      selectionList: {
        region: emptySelectionList(),
        country: emptySelectionList(),
        msa: emptySelectionList(),
        city: emptySelectionList(),
        rics_k10: emptySelectionList(),
        rics_k50: emptySelectionList(),
        rics_k400: emptySelectionList(),
        company: emptySelectionList(),
        seniority: emptySelectionList(),
        ethnicity: emptySelectionList(),
        gender: emptySelectionList(),
        highest_degree: emptySelectionList(),
        skill_k75: emptySelectionList(),
        skill_k700: emptySelectionList(),
        skill_k3000: emptySelectionList(),
      },
    },
  });
});

export const ReferenceListsHandler = graphql.query('ReferenceLists', () => {
  return HttpResponse.json({
    data: {
      highestDegree: emptySelectionList(),
      ethnicity: emptySelectionList(),
      gender: emptySelectionList(),
      seniority: emptySelectionList(),
    },
  });
});

export const RoleTaxonomyVersionsHandler = graphql.query(
  'RoleTaxonomyVersions',
  () => {
    return HttpResponse.json({
      data: {
        roleTaxonomies: [
          {
            taxonomyId: 0,
            taxonomyName: 'Default',
            version: '3.1',
            isLatest: true,
            __typename: 'RoleTaxonomyMetadata',
          },
        ],
      },
    });
  }
);

export const TaxonomyListHandler = graphql.query('TaxonomyList', () => {
  return HttpResponse.json({
    data: {
      taxonomyList: {
        taxonomies: [
          {
            id: 0,
            name: 'Default',
            version: '3.1',
          },
        ],
      },
    },
  });
});

export const TaxonomyLatestVersionHandler = graphql.query(
  'TaxonomyLatestVersion',
  () => {
    return HttpResponse.json({
      data: {
        taxonomyLatestVersion: {
          version: '3.1',
        },
      },
    });
  }
);

export const newsletterSignupHandler = http.post(
  '*/api/users/zoho/newslettersignup',
  () => {
    return HttpResponse.json({ ok: true });
  }
);

/** Saved DataBuilder datasets list, served by the data-builder backend. */
export const dataBuilderDatasetsListHandler = http.get(
  '*/proxy/deliverables/list-datasets',
  () => HttpResponse.json([])
);

// Opt-in passthrough: when set, the impact handler delegates to the
// real network while every other handler keeps intercepting. Lets you
// hit live TVL impact data without losing the mocked shims. Toggle in
// DevTools:
//   localStorage.setItem('impact-live', 'true'); location.reload()
// Clear with:
//   localStorage.removeItem('impact-live'); location.reload()
const IMPACT_LIVE_STORAGE_KEY = 'impact-live';
const IMPACT_LIVE_STORAGE_ON = 'true';

export function shouldLetImpactThrough() {
  return (
    typeof localStorage !== 'undefined' &&
    localStorage.getItem(IMPACT_LIVE_STORAGE_KEY) === IMPACT_LIVE_STORAGE_ON
  );
}

export const impactHandler = http.get('*/api/v1/impact', ({ request }) => {
  if (shouldLetImpactThrough()) {
    return passthrough();
  }
  const url = new URL(request.url);
  return HttpResponse.json(
    filterImpactResponse({
      fips: url.searchParams.get('fips') || undefined,
      region: url.searchParams.get('region') || undefined,
      bea: url.searchParams.get('bea') || undefined,
      naics: url.searchParams.get('naics') || undefined,
    })
  );
});

/**
 * `renderWithData` uses urql with `url: '/'`. Without a handler, unmocked operations
 * warn on every attempt and can amplify retries + act noise. Specific `graphql.query`
 * handlers registered earlier still win; this only catches unmatched operations.
 */
const graphqlOperationFallbackHandler = graphql.operation(() =>
  HttpResponse.json({ data: {} })
);

export const handlers = [
  // Handles a "GetUserInfo" query
  GetRevelioAdminUserDetailsHandler,
  GetAllUsersHandler,
  whoAmIHandler,
  GetClientDetailsHandler,
  companySelectionListHandler,
  reportConfigHandler(),
  SelectionListsHandler,
  ReferenceListsHandler,
  RoleTaxonomyVersionsHandler,
  TaxonomyListHandler,
  TaxonomyLatestVersionHandler,
  newsletterSignupHandler,
  dataBuilderDatasetsListHandler,
  impactHandler,
  graphqlOperationFallbackHandler,
];
